import styles from './testPlanMainHeader.module.scss';
import {
  useDispatch,
  useSelector
} from "react-redux";
import { useTranslation } from "react-i18next";
import {
  MouseEvent,
  useEffect,
  useState
} from "react";
import {
  AppDispatch,
  RootState
} from "../../../store";
import { useGetAllTestCaseTemplateQuery } from "../../../api/testCaseTemplateService";
import { Accordion } from "../../../shared/components/accordion/accordion";
import arrowIcon from "../../../shared/images/arrow.svg";
import closeIcon from "../../../shared/images/close.svg";
import {
  deleteSelectedTestCaseTemplate,
  setSelectedTestCase
} from "../../../store/slices/testCaseTemplateSlice";
import { deleteTestPlanExecuteResponse } from "../../../store/slices/testPlanSlice";
import { TestPlanHeaderScenarioItem } from "./testPlanHeaderScenarioItem/testPlanHeaderScenarioItem";
import { TransformTestCase } from "../../../types/testCaseTemplateTypes";
import { TestPlanScenario } from "../../../types/testPlanTypes";
import { PassedAndFailedResultFilter } from "../../../components/passedAndFailedResultFilter/passedAndFailedResultFilter";
import { StatusCodeFilterResult } from "../../../components/statusCodeFilterResult/statusCodeFilterResult";
import { TotalExecutionTime } from "../../../components/totalExecutionTime/totalExecutionTime";
import {
  ResultEnum,
  TransformExecuteResponse
} from "../../../types/clientTypes";
import { selectTestPlanExecuteResponses } from "../../../store/selectors";
import { StatusCode } from "../../../types/sharedTypes";
import {SearchInput} from "../../../shared/components/searchInput/searchInput";

export const TestPlanMainHeader = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();

  const selectedTestCase = useSelector((state: RootState) => state.testCaseTemplateSlice.selectedTestCase);

  const selectedTestPlan = useSelector((state: RootState) => state.testPlanSlice.selectedTestPlan)
  const selectedScenarios = useSelector((state: RootState) => state.testPlanSlice.selectedScenarios);
  const testPlanExecuteResponses = useSelector(selectTestPlanExecuteResponses);

  const [searchValue, setSearchValue] = useState<string>("");
  const [needResetFilter, setNeedResetFilter] = useState(true);

  const [localSelectedScenarios, setLocalSelectedScenarios] = useState<Array<TestPlanScenario>>([]);
  const [resultFilter, setResultFilter] = useState<ResultEnum>();
  const [statusCodeFilter, setStatusCodeFilter] = useState<StatusCode>();
  const [filteredSelectedScenarios, setFilteredSelectedScenarios] = useState<Array<TestPlanScenario>>([]);
  const [filteredExecuteResponses, setFilteredExecuteResponses] = useState<Array<TransformExecuteResponse>>([]);

  const { data: testCaseTemplates } = useGetAllTestCaseTemplateQuery("");

  useEffect(() => {
    setLocalSelectedScenarios(
      selectedScenarios.filter(item => !item.isDeleted)
      .filter(item => !!item.assignedRequests.length)
    );
    if (needResetFilter) {
      setResultFilter(undefined);
      setStatusCodeFilter(undefined);
    } else {
      setNeedResetFilter(true);
    }
  }, [selectedScenarios, setLocalSelectedScenarios, setNeedResetFilter]);

  useEffect(() => {
    setFilteredSelectedScenarios(localSelectedScenarios);
  }, [setFilteredSelectedScenarios, localSelectedScenarios]);

  useEffect(() => {
    const newFilteredSelectedScenarios = localSelectedScenarios.map(scenario => {
      const newScenario = { ...scenario };
      newScenario.testCases = newScenario.testCases
          .filter(item => item.name.toLowerCase().includes(searchValue.toLowerCase()))
      if (!!resultFilter) {
        const resultsIds = testPlanExecuteResponses
          .filter(item => item.result === resultFilter || !!item.isLoad)
          .map(item => item.internalId);
        newScenario.testCases = newScenario.testCases
          .filter(testCase => resultsIds.includes(testCase.internalId));
      }
      if (!!statusCodeFilter) {
        const resultsIds = testPlanExecuteResponses
          .filter(item => item.statusCode === statusCodeFilter.code || !!item.isLoad)
          .map(item => item.internalId);
        newScenario.testCases = newScenario.testCases
          .filter(testCase => resultsIds.includes(testCase.internalId));
      }
      return newScenario.testCases.length !== scenario.testCases.length ? newScenario : scenario;
    });

    const internalIds: Array<string | undefined> = [];
    newFilteredSelectedScenarios.forEach(scenario => {
      internalIds.push(...scenario.testCases.map(item => item.internalId));
    });

    setFilteredExecuteResponses(testPlanExecuteResponses.filter(item => internalIds.includes(item.internalId)));
    setFilteredSelectedScenarios(newFilteredSelectedScenarios.filter(item => item.testCases.length));
  }, [searchValue, resultFilter, testPlanExecuteResponses, statusCodeFilter, localSelectedScenarios, setFilteredSelectedScenarios]);

  useEffect(() => {
    let findTestCase: TransformTestCase | undefined;
    filteredSelectedScenarios.some(scenario => {
      findTestCase = scenario.testCases.find(testCase => testCase.internalId === selectedTestCase.internalId);
      return !!findTestCase
    });
    if (!!findTestCase) {
      const template = testCaseTemplates?.find(item => item.id === selectedTestCase.testCaseTemplateId);
      if (!!template) {
        dispatch(setSelectedTestCase({ testCase: findTestCase, template }));
      } else {
        console.error("Error find template in test plans")
      }
    } else {
      let newSelectedTestCase: TransformTestCase | undefined;
      filteredSelectedScenarios.some(scenario => {
        if (scenario.testCases.length > 0) {
          newSelectedTestCase = scenario.testCases[0];
          return true;
        } else {
          return false;
        }
      });

      if (!!newSelectedTestCase) {
        const template = testCaseTemplates?.find(item => item.id === newSelectedTestCase?.testCaseTemplateId)
        if (template) {
          dispatch(setSelectedTestCase({ testCase: newSelectedTestCase, template }));
        } else {
          console.error("Error find template in test plans")
        }
      } else {
        dispatch(deleteSelectedTestCaseTemplate());
      }
    }

  }, [filteredSelectedScenarios, dispatch]);

  const clickClearAll = (e: MouseEvent<HTMLDivElement>): void => {
    e.stopPropagation();
    dispatch(deleteTestPlanExecuteResponse())
  }

  return (
    <div className={styles.container}>
      <Accordion activeClassName={styles.active}
                 activeIndex={0}
                 disabledAutoScroll
                 maxHeight={400}
                 data={
        [{
          title: (
            <div className={styles.accordionTitle}>
              <div className={styles.main}>
                <img src={arrowIcon} alt=""
                     className={styles.arrow}/>
                {t("common.results")} {selectedTestPlan.name}
                <div className={styles.search} onClick={(e) => e.stopPropagation()}>
                  <SearchInput value={searchValue} onChange={setSearchValue}/>
                </div>
              </div>
              <div className={styles.status}>
                { testPlanExecuteResponses.length !== 0 && (
                    <StatusCodeFilterResult onChangeFilter={setStatusCodeFilter}
                                            filter={statusCodeFilter}
                                            executeResponses={filteredExecuteResponses}/>
                ) }
              </div>
              <div className={styles.result}>
                { testPlanExecuteResponses.length !== 0 && (
                    <PassedAndFailedResultFilter executeResponses={filteredExecuteResponses}
                                                 filter={resultFilter}
                                                 onChangeFilter={setResultFilter}/>
                ) }
              </div>
              <div>
                { testPlanExecuteResponses.length !== 0 && (
                    <TotalExecutionTime executeResponses={filteredExecuteResponses}
                                        maximumExecutionTimeout={selectedTestPlan.maximumExecutionTimeout}/>
                )}
              </div>
              <div className={styles.clearBlock} onClick={clickClearAll}>
                <img src={closeIcon} alt=""/>
                {t("common.clearAll")}
              </div>
            </div>
          ),
          child: (
            <div className={styles.items}>
              { filteredSelectedScenarios.length
                  ? filteredSelectedScenarios.map(((scenario, index) => (
                  <TestPlanHeaderScenarioItem
                    key={index}
                    onClickRerun={() => setNeedResetFilter(false)}
                    testCaseTemplates={testCaseTemplates}
                    scenario={scenario}/>
                )))
                  : <div className={styles.noResults}>{t("common.noResult")}</div>
              }
            </div>
          )
        }]
      }/>
    </div>
  )
}